import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import NextLink from 'next/link';
import { Variant } from 'shared/types/product';
import { Product } from 'shared/types/product/Product';
import { LineItem } from 'shared/types/wishlist/LineItem';
import OutOfStock from 'components/commercetools-ui/atoms/out-of-stock';
import QuickView from 'components/commercetools-ui/organisms/product/product-quick-view';
import Prices from 'components/commercetools-ui/organisms/product/product-tile/prices';
import WishlistButton from 'components/commercetools-ui/organisms/wishlist/components/wishlist-button';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useVariantWithDiscount from 'helpers/hooks/useVariantWithDiscount';
import { desktop } from 'helpers/utils/screensizes';
import Image from 'frontastic/lib/image';
import useTrack from './useTrack';
import { ShareIcon } from '@heroicons/react/24/outline';
import { ITEM_CODE, ITEM_NAME } from 'helpers/constants/attributes';
import ColorSwatches from './swatches';
import usePath from 'helpers/hooks/usePath';
import { useParams } from 'next/navigation';
import Button from 'components/commercetools-ui/atoms/button';
import ShopOnline from '../shop-online';
import { localeMapping } from 'helpers/constants/localeMapping';
import useWindowWidth from 'helpers/hooks/useBrowerWidth';
import { getPdpUrl } from 'helpers/utils/pdpUrlHelper';
import { useRouter } from 'next/navigation';
import { sessionStorageKeys } from 'helpers/constants/sessionStorageKeys';
import Typography from 'components/commercetools-ui/atoms/typography';
import ColorSwatchesDropdown from './colorswatchesdropdown';


interface ProductTileProps {
  product: Product;
  onClick?: () => void;
  isSearchResult?: boolean;
  isCollectionPage?: boolean;
  isCategoryPage?: boolean;
  disableQuickView?: boolean;
  disableWishlistButton?: boolean;
  disableVariants?: boolean;
  images?: string[];
  globalView?: string;
}

const ProductTile: FC<ProductTileProps> = ({
  product,
  images,
  onClick,
  isSearchResult = false,
  isCollectionPage = false,
  isCategoryPage = false,
  disableQuickView = false,
  disableWishlistButton = false,
  disableVariants = false,
  globalView,
}) => {
  const { locale } = useParams();
  const localeKey = localeMapping[locale];
  const [isDesktopSize] = useMediaQuery(desktop);
  const { ref } = useTrack({ product });
  const variantWithDiscount = useVariantWithDiscount(product.variants) as Variant;
  const [selectedVariant, setSelectedVariant] = useState(
    () => product?.variants?.filter((variant) => variant.id === product?.masterVariantID)?.[0] || product.variants?.[0],
  );
  const width = useWindowWidth();
  const hasDiscount =
    selectedVariant?.discountedPrice?.centAmount &&
    selectedVariant?.price?.centAmount &&
    selectedVariant?.discountedPrice?.centAmount < selectedVariant?.price?.centAmount;

  const discountedPrice = selectedVariant?.discountedPrice;
  const { path } = usePath();
  const discountPercentage = selectedVariant
    ? (((selectedVariant.price?.centAmount as number) - (discountedPrice?.centAmount as number)) /
        (selectedVariant.price?.centAmount as number)) *
      100
    : 0;

  const [imageHovered, setImageHovered] = useState(false);
  const [buttonHovered, setButtonHovered] = useState(false);
  const buttonIsVisible = useMemo(
    () => (imageHovered || buttonHovered) && isDesktopSize,
    [imageHovered, buttonHovered, isDesktopSize],
  );
  const hideButton = () => {
    setImageHovered(false);
    setButtonHovered(false);
  };
  const productToWishlistLineItem = useMemo<LineItem | undefined>(() => {
    if (product) {
      return {
        lineItemId: product.productId ?? '',
        productId: product.productId,
        name: product.name,
        count: 1,
        variant: selectedVariant,
        addedAt: new Date(),
        _url: product._url,
      };
    }
  }, [product, selectedVariant]);

  
  const productUrl = useMemo(() => {    
    const pUrl =  product._url || getPdpUrl(product.slug, selectedVariant?.sku, selectedVariant?.attributes?.colour);

    return (
      pUrl +
      (isSearchResult ? '?sr=1' : '')
    );
  }, [product, selectedVariant, isSearchResult]);

  const router = useRouter();

  const handleProductTileClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if(e.altKey){
      return; // Let the browser handle it
    }
    else{
      e.preventDefault(); 
      e.stopPropagation(); // Stop default navigation  
      if (e.metaKey || // Cmd on Mac
        e.ctrlKey || // Ctrl on Windows/Linux
        e.shiftKey
      ) {
        const destinationUrl = isCategoryPage ? productUrl+'?category='+path :  isCollectionPage ? productUrl+'?collection='+path : productUrl;
        window.open(`/${locale}${destinationUrl}`, '_blank');    
      }else{
        sessionStorage.setItem(sessionStorageKeys.categoryLink, '');
        sessionStorage.setItem(sessionStorageKeys.collectionLink, '');
        if(isCategoryPage) sessionStorage.setItem(sessionStorageKeys.categoryLink, path);
        else if(isCollectionPage) sessionStorage.setItem(sessionStorageKeys.collectionLink, path);
        router.push(`/${locale}${productUrl}`);
      }
    }
  }


  interface PriceDetailsProps {
    item: any,
  }
  const PriceDetails: React.FC<PriceDetailsProps> = ({ item }) => {
    let countryCode = 'NZD';
    if (locale == 'au') {
      countryCode = 'AUD';
    }
    const priceValue =      
      item?.prices?.[countryCode]?.priceValues?.[0]?.value;
    return (
      <div className='flex gap-5 items-center text-8 md:text-11 2xl:text-13'>
        <Typography className="whitespace-normal">
          {priceValue ? ((priceValue / 100).toFixed(2)) : ""}
        </Typography>
      </div>
    );
  };


  type Props = {
    selectedVariant: {
      images?: string[];
    };
    product: {
      name?: string;
    };
  };


  const images11 = selectedVariant?.images && selectedVariant.images.length > 0
    ? selectedVariant.images
    : ['/images/no-image-placeholder/product-thumbnail.jpg'];

  const [currentIndex, setCurrentIndex] = useState(0);

  const prevImage = () => {
    setCurrentIndex((prev) => (prev === 0 ? images11.length - 1 : prev - 1));
  };

  const nextImage = () => {
    setCurrentIndex((prev) => (prev === images11.length - 1 ? 0 : prev + 1));
  };



  return (
    <div ref={ref}>
      <NextLink href={`/${locale}${productUrl}`} prefetch={true}> 
        
        
        {/* GRID - VIEW
        --------------------- */}
        {globalView === 'grid' && (
          <div onClick={handleProductTileClick}>
            <div className="relative">
              <div className="relative w-full" onMouseEnter={() => setImageHovered(true)} onMouseLeave={() => setImageHovered(false)} >
                  <div className="relative block w-full xl:h-[10rem] 2xl:h-[23rem] bg-white" style={{ paddingBottom: '100%' }}>
                  
                  {/* Image */}
                  <Image
                    priority
                    src={encodeURI(images11[currentIndex].toString())}
                    alt={product.name ?? ''}
                    style={{ objectFit: 'cover', objectPosition: 'center' }}
                    className="w-full rounded-sm group-hover:opacity-75 "
                    fill
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    onError={(event: any) => {
                      event.target.id = '/images/no-image-placeholder/search-result-thumbnail.png';
                      event.target.srcset = '/images/no-image-placeholder/search-result-thumbnail.png';
                    }}
                  />

                  {/* Prev arrow */}
                  {images11.length > 1 && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        prevImage();
                      }}
                      className="absolute top-1/2 left-2 transform -translate-y-1/2 bg-gray text-black p-2 rounded-full text-xl"
                      aria-label="Previous image"
                      style={{ lineHeight: 1 }}
                    >
                      &lt;
                    </button>
                  )}

                  {/* Next arrow */}
                  {images11.length > 1 && (
                    <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      nextImage();
                    }}
                      className="absolute top-1/2 right-2 transform -translate-y-1/2 bg-gray text-black p-2 rounded-full text-xl"
                      aria-label="Next image"
                      style={{ lineHeight: 1 }}
                    >
                      &gt;
                    </button>
                  )}

                </div>
              </div>
            </div>

            <div
              className={`h-[12rem] ${
                imageHovered ? 'bg-[#F3EDE9]' : isCollectionPage ? 'bg-white' : 'bg-[#F8F8F8]'
              } ] 2xl:px-8 xl:px-8 lg:px-8 md:px-8 px-16 pt-16 hover:bg-[#F3EDE9]`}
            >
              <div className="flex h-[8rem] flex-col justify-between 2xl:h-[9rem]">
                <div className="flex items-center justify-between">
                  <span className="text-15 font-normal leading-6 text-[#000000]">
                    {product?.categories &&
                      (product?.categories[0]?.name ||
                        product?.categories?.[1]?.categoryId ||
                        product?.categories[0]?.categoryId)}
                  </span> 
                  <span className="flex cursor-pointer font-normal leading-6 text-[#333]"
                    onClick={(e) => e.preventDefault()}>
                    <ShopOnline itemCode={product?.variants?.[0]?.attributes?.[ITEM_CODE].toString()} />
                  </span>
                </div>
                <div className="block max-w-[80%]  md:max-w-[100%] ">
                  <p className="line-clamp-2 text-14 font-medium capitalize leading-[1.625rem] text-[#2B2C39] md:text-12 lg:text-primary-black  2xl:text-14">
                    {product?.attributesAlgolia?.[ITEM_NAME]?.[localeKey] || product?.name}
                  </p>
                </div>
                <div className={`mt-3 flex gap-2 md:gap-0 lg:gap-0 xl:gap-0 2xl:gap-0 items-center justify-between text-11 font-medium text-[#1C1C1C] md:text-12 2xl:text-14`}>
                  <span>
                    <span className="text-[#666]">Product code</span>{' '}
                    {product?.attributesAlgolia?.[ITEM_CODE] || selectedVariant?.attributes?.[ITEM_CODE]}
                  </span>
                </div>

                <div className="relative z-10 mt-3 flex gap-2 items-center justify-between text-11 font-medium text-[#1C1C1C] md:text-12 2xl:text-13">
                    <div className="flex items-center gap-2">
                      <span className="text-[#666] text-12">RRP $</span>
                      <PriceDetails item={selectedVariant} />
                    </div>

                    {product?.variants && (
                      <span onClick={(e) => {
                          e.preventDefault();   
                          e.stopPropagation();  
                        }}
                      >
                        <ColorSwatchesDropdown
                          product={product}
                          variants={product?.variants}
                          setSelectedVariant={setSelectedVariant}
                          viewMode={"list"}
                        />
                      </span>
                    )}
                </div>
              </div>
            </div>
          </div>
        )}



{/* ------------------------------------------------------------------------------------------------------------------------------------------------------------------------
------------------------------------------------------------------------------------------------------------------------------------------------------------------------ */}


        {/* LIST - VIEW
        ------------------------- */}
        {globalView === 'list' && (
          <div className="flex flex-wrap lg:flex-nowrap bg-white hover:bg-gray-100 border-t border-gray-300 w-full mb-5">
            {/* Product Name */}
            <div className="px-6 py-4 border-gray-300 text-sm text-gray-900 flex items-center gap-4 w-full lg:w-[60%]">
              {/* Image */}
              <div className="flex-shrink-0 w-90 h-90 relative">
                <Image
                  priority
                  src={
                    selectedVariant?.images?.[0]
                      ? encodeURI(selectedVariant?.images?.[0].toString())
                      : '/images/no-image-placeholder/product-thumbnail.jpg'
                  }
                  alt={product.name ?? ''}
                  width={264}
                  height={264}
                  style={{ objectFit: 'contain', objectPosition: 'center' }}
                  className="rounded-sm group-hover:opacity-75"
                  sizes="64px"
                  onError={(event: any) => {
                    event.target.id = '/images/no-image-placeholder/search-result-thumbnail.png';
                    event.target.srcset = '/images/no-image-placeholder/search-result-thumbnail.png';
                  }}
                />
              </div>
          
              {/* Text Section */}
              <div className="flex flex-col justify-center">
                {/* Top: Category */}
                <div className="text-sm font-normal text-gray-700 leading-5 ${NationalLight.className">
                  {product?.categories &&
                    (product?.categories[0]?.name ||
                      product?.categories?.[1]?.categoryId ||
                      product?.categories[0]?.categoryId)}
                </div>
          
                {/* Bottom: Product Name */}
                <div className="text-base font-medium text-gray-900 leading-6">
                  {product?.attributesAlgolia?.[ITEM_NAME]?.[localeKey] || product?.name}
                </div>

                <div className="mt-3 items-center justify-between text-12 font-medium text-[#1C1C1C] md:text-14 2xl:text-16">
                  <span className="flex ml-2">
                    {product?.variants && (
                      <span className="mr-5" onClick={(e) => {
                          e.preventDefault();   
                          e.stopPropagation();  
                        }}
                      >
                        <ColorSwatchesDropdown
                          product={product}
                          variants={product?.variants}
                          setSelectedVariant={setSelectedVariant}
                          viewMode={"list"}
                        />
                      </span>
                    )}
                    <span className="flex cursor-pointer font-normal leading-6 text-[#333]"
                      onClick={(e) => e.preventDefault()}>
                      <ShopOnline itemCode={product?.variants?.[0]?.attributes?.[ITEM_CODE].toString()} />
                    </span>
                  </span>

                </div>
              </div>


            </div>
          
            {/* Product Code */}
            <div className="px-10 py-4 border-gray-300 text-sm text-gray-900 align-middle whitespace-nowrap w-full lg:w-[20%] flex items-center">
              {product?.attributesAlgolia?.[ITEM_CODE] || selectedVariant?.attributes?.[ITEM_CODE]}
            </div>
          
            {/* RRP */}
            <div className="px-10 py-4 text-sm text-gray-900 align-middle whitespace-nowrap w-full lg:w-[20%] flex items-center text-center">
              $<PriceDetails item={selectedVariant} />
            </div>
          </div>
        )}



      </NextLink>
      </div>
  );
};

export default ProductTile;
